import classNames from "classnames";

import type { PaymentSource } from "../../../hooks/useBilling";
import type { Estimate } from "../../../hooks/useCheckout";
import type { SeatBasedTeamSubscription } from "../../../hooks/useTeamsTypes";
import { PaymentType } from "../../../lib/ChargebeeAdapter";
import { formatDueOnDate } from "../../../utils/convertUtils";
import { CheckoutSummaryRow } from "../../ChargebeeCheckout/components/CheckoutSummaryRow/CheckoutSummaryRow";
import { EstimateSummaryPeriod } from "../EstimateSummaryPeriod/EstimateSummaryPeriod";

import style from "./SeatUpgradeEstimateSummary.module.css";

export const SeatUpgradeEstimateSummary: React.FC<{
  proratedEstimate?: Estimate;
  renewalEstimate: Estimate;
  renewalDate: string;
  currencySymbol?: string;
  isLoading: boolean;
  additionalEditorSeatCount: number;
  subscription: SeatBasedTeamSubscription;
  paymentSource: PaymentSource;
}> = ({
  proratedEstimate,
  renewalEstimate,
  renewalDate,
  currencySymbol,
  isLoading,
  additionalEditorSeatCount,
  subscription,
  paymentSource,
}) => {
  return (
    <>
      <div className={style.summary}>
        <span className={style.summaryTitle}>Summary</span>
        {subscription.isTrial && renewalEstimate.trialEnd && (
          <>
            <CheckoutSummaryRow
              name={`Due on ${formatDueOnDate(renewalEstimate.trialEnd)} (with tax)`}
              price={renewalEstimate.totalAmount}
              currencySymbol={currencySymbol}
              isLoading={isLoading}
            />
            <div className={classNames(style.tertiary, style.priceBreakdown)}>
              <span>{`${renewalEstimate.editorSeatCount - 1} editor seat \u00d7 ${currencySymbol}${
                renewalEstimate.unitAmount
              } \u00d7 `}</span>
              <EstimateSummaryPeriod subscriptionPeriod={renewalEstimate.period} />
              <br />
              <span>{`Your owner seat \u00d7 ${currencySymbol}${renewalEstimate.unitAmount} \u00d7 `}</span>
              <EstimateSummaryPeriod subscriptionPeriod={renewalEstimate.period} />
            </div>
            <div className={style.trialWrapper}>
              <hr className={style.horizontalLine} />
              <CheckoutSummaryRow
                isPriceHighlighted={true}
                name="Total due today"
                price={proratedEstimate?.totalAmount ?? "0"}
                currencySymbol={currencySymbol}
              />
            </div>
          </>
        )}
        {!subscription.isTrial && (
          <>
            <CheckoutSummaryRow
              name="Due today (with tax)"
              price={proratedEstimate?.totalAmount}
              priceTooltipText="The total price is prorated based on the days left until your next bill."
              currencySymbol={currencySymbol}
              isLoading={isLoading}
            />
            <div className={classNames(style.tertiary, style.priceBreakdown)}>
              <span>{`${additionalEditorSeatCount} editor seat \u00d7 ${currencySymbol}${renewalEstimate.unitAmount} \u00d7 `}</span>
              <EstimateSummaryPeriod
                subscriptionPeriod={renewalEstimate.period}
                partialDurationMs={new Date(renewalDate).getTime() - Date.now()}
              />
            </div>
          </>
        )}
      </div>
      {!renewalEstimate.trialEnd && (
        <p className={classNames(style.tertiary, style.additionalInfo)}>
          {paymentSource.type === PaymentType.Card && (
            <span>{`Your card ending in ${paymentSource.lastFourDigits} will be charged when you click Invite. `}</span>
          )}
          {paymentSource.type === PaymentType.ApplePay && (
            <span>{`The card you use with Apple Pay will be charged when you click Invite. `}</span>
          )}
          <br />
          <span>{`All seats renew at full price. `}</span>
          <span>{`Your card will be charged ${renewalEstimate.period} unless you cancel your subscription. `}</span>
          <span>{`The next payment is going to be ${currencySymbol}${renewalEstimate.totalAmount}. `}</span>
          {renewalEstimate.currencyCode === "USD" && (
            <span>{`All prices in ${renewalEstimate.currencyCode}.`}</span>
          )}
        </p>
      )}
      {renewalEstimate.trialEnd && (
        <p className={classNames(style.tertiary, style.additionalInfo)}>
          <span>{`Your trial ends on ${formatDueOnDate(renewalEstimate.trialEnd)}. `}</span>
          <span>{`All seats renew at full price. `}</span>
          {paymentSource.type === PaymentType.Card && (
            <span>{`Your card ending in ${paymentSource.lastFourDigits} will be charged ${renewalEstimate.period} after your trial ends unless you cancel your subscription. `}</span>
          )}
          {paymentSource.type === PaymentType.ApplePay && (
            <span>{`The card you use with Apple Pay will be charged ${renewalEstimate.period} after your trial ends unless you cancel your subscription. `}</span>
          )}
          <span>{`The next payment is going to be ${currencySymbol}${renewalEstimate.totalAmount}. `}</span>
          {renewalEstimate.currencyCode === "USD" && (
            <span>{`All prices in ${renewalEstimate.currencyCode}.`}</span>
          )}
        </p>
      )}
    </>
  );
};
